import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { addPairCode, deletePairCode, listPairCodes } from '../../../api/admin.paircodes'
import type { DevicePairCode } from '../../../api/admin.paircodes'
import { QueryBoundary } from '../../../components/common/QueryBoundary'
import { Skeleton } from '../../../components/ui/skeleton'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'

const statusMeta: Record<DevicePairCode['status'], { label: string; cls: string }> = {
  active: { label: '未使用', cls: 'bg-emerald-400/10 text-emerald-300' },
  used: { label: '已绑定', cls: 'bg-cyan-400/10 text-cyan-300' },
  revoked: { label: '已吊销', cls: 'bg-rose-400/10 text-rose-300' },
}

function fmtTime(ms?: number) {
  if (!ms) return '—'
  return new Date(ms).toLocaleString('zh-CN', { hour12: false })
}

export function PairCodesSection() {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: ['v2', 'device-pair-codes'],
    queryFn: listPairCodes,
    retry: 1,
  })

  const [note, setNote] = useState('')
  const [code, setCode] = useState('')
  const [lastIssued, setLastIssued] = useState<string | null>(null)

  const addMut = useMutation({
    mutationFn: addPairCode,
    onSuccess: (created) => {
      setLastIssued(created.code)
      setCode('')
      setNote('')
      void queryClient.invalidateQueries({ queryKey: ['v2', 'device-pair-codes'] })
    },
  })

  const delMut = useMutation({
    mutationFn: deletePairCode,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['v2', 'device-pair-codes'] })
    },
  })

  return (
    <div className="space-y-4">
      <div className="panel-glass rounded-xl p-4">
        <div className="mb-3 text-xs text-muted-foreground">
          发放配对码（一码一设备，设备首次配对时绑定；备注建议写站点/区域/负责人）。
          配对码留空则自动生成 <span className="font-mono">SPC-XXXXXXXX</span>。
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label htmlFor="pair-code-note">备注（必填，≤128 字）</Label>
            <Input
              id="pair-code-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="如 乌什站-001 / 负责人阿不都拉"
              className="w-64"
            />
          </div>
          <div>
            <Label htmlFor="pair-code-input">配对码（可选）</Label>
            <Input
              id="pair-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="留空自动生成"
              className="w-44 font-mono"
            />
          </div>
          <Button
            size="sm"
            disabled={addMut.isPending || note.trim().length === 0}
            onClick={() => addMut.mutate({ code: code.trim() || undefined, note: note.trim() })}
          >
            {addMut.isPending ? '发放中…' : '发放配对码'}
          </Button>
        </div>
        {lastIssued && (
          <div className="mt-3 rounded-lg border border-emerald-400/20 bg-emerald-400/[.06] px-3 py-2 text-xs text-emerald-300">
            已发放：<span className="font-mono font-semibold">{lastIssued}</span>
            （请登记到站点台账后交付安装员）
          </div>
        )}
        {addMut.isError && (
          <div className="mt-3 text-xs text-rose-300">发放失败：{(addMut.error as Error).message}</div>
        )}
      </div>

      <QueryBoundary
        query={query}
        skeleton={<Skeleton className="h-48 rounded-xl" />}
        isEmpty={(items) => items.length === 0}
        empty={{ title: '尚无配对码', description: '先在上方发放一枚配对码' }}
      >
        {(items) => (
          <div className="panel-glass overflow-hidden rounded-xl">
            <table className="w-full text-left">
              <thead className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2.5 font-medium">配对码</th>
                  <th className="px-3 py-2.5 font-medium">备注</th>
                  <th className="px-3 py-2.5 font-medium">状态</th>
                  <th className="px-3 py-2.5 font-medium">绑定设备</th>
                  <th className="px-3 py-2.5 font-medium">绑定时间</th>
                  <th className="px-3 py-2.5 font-medium">发放人</th>
                  <th className="px-3 py-2.5 font-medium" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border/70">
                {items.map((c) => {
                  const meta = statusMeta[c.status]
                  return (
                    <tr key={c.code} className="text-xs hover:bg-white/[.03]">
                      <td className="px-3 py-2.5 font-mono font-semibold text-primary">{c.code}</td>
                      <td className="px-3 py-2.5 text-foreground/90">{c.note || '—'}</td>
                      <td className="px-3 py-2.5">
                        <span className={`rounded-md px-2 py-1 text-[10px] ${meta.cls}`}>{meta.label}</span>
                      </td>
                      <td className="px-3 py-2.5 font-mono text-muted-foreground">{c.boundDeviceId || '—'}</td>
                      <td className="px-3 py-2.5 text-muted-foreground">{fmtTime(c.boundAt)}</td>
                      <td className="px-3 py-2.5 text-muted-foreground">{c.createdBy || '—'}</td>
                      <td className="px-3 py-2.5 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-rose-300 hover:text-rose-200"
                          disabled={delMut.isPending}
                          onClick={() => {
                            if (confirm(`删除配对码 ${c.code}？删除后该码立即失效。`)) {
                              delMut.mutate(c.code)
                            }
                          }}
                        >
                          删除
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </QueryBoundary>
    </div>
  )
}
